/**
 * The dashboard's writes. Everything here goes straight to Firestore from the browser —
 * the rules proven in B-07 are what make that safe, and the Admin SDK is not involved.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * The concurrency question B-14 was told to settle (D-60).
 *
 * D-48 made the bot's appends transactional but left `putWorkout` overwriting a whole
 * day wholesale, which was harmless while one client existed. It is not harmless now: a
 * set logged in WhatsApp while the editor is open would be erased by the next Save, and
 * silently — the day would simply be missing an exercise nobody deleted.
 *
 * The fix here is a compare-and-set on `updatedAt` rather than a merge. Merging is not
 * well defined for this shape: if a set was edited here and the same exercise changed
 * there, no rule picks a winner that the user would recognise as correct. Refusing is
 * honest, and cheap to recover from — the editor is subscribed to the same document, so
 * it already has the newer version on screen when it says so.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import { deleteDoc, doc, getDoc, runTransaction, serverTimestamp, setDoc, updateDoc, writeBatch, type Firestore } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import type {
  CustomExercise,
  DateString,
  ExerciseSet,
  GymCosts,
  GymRef,
  MuscleGroup,
  OnboardingAnswers,
  Plan,
  UserProfile,
  Workout,
} from "@altrtrack/core";
import {
  DEFAULT_BODYWEIGHT_KG,
  DEFAULT_TIMEZONE,
  emptyWorkout,
  gymId,
  isCustomId,
  isPlan,
  isValidGymCosts,
  normaliseHealth,
  profilePatch,
  scoreWorkout,
} from "@altrtrack/core";
import { db, functions } from "./firebase.js";
import { withPt, withSetAdded, withSetRemoved, withSetUpdated } from "./today.js";

/**
 * Create the profile document if signing in got here before the bot ever did.
 *
 * Since D-61 the portal is a front door in its own right: someone can verify their
 * number and arrive with no `users/{uid}` document at all, because nothing has ever
 * messaged WhatsApp for them. Without this they would see a working, permanently empty
 * app. The bot's `ensureUser` is the mirror of this and is equally careful not to
 * overwrite — whichever door is used first wins, and the second one no-ops.
 *
 * The timezone comes from the browser, which actually knows it, rather than from the
 * default the bot has to assume. Browsers may report a legacy alias — Chrome says
 * "Asia/Calcutta" where the bot's default says "Asia/Kolkata" — and that is fine:
 * `Intl` resolves both to the same zone, and the zone is only ever used to work out
 * which local day a timestamp falls on.
 */
export async function ensureProfile(uid: string, phoneNumber: string): Promise<void> {
  const ref = doc(db, "users", uid);
  await runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) return;
    const profile: UserProfile = {
      phoneNumber,
      currentBodyweightKg: DEFAULT_BODYWEIGHT_KG,
      timezone: browserTimezone(),
      createdAt: Date.now(),
    };
    tx.set(ref, profile);
  });
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

export type SaveResult =
  | { ok: true }
  /** Someone else — almost certainly the bot — wrote this day while it was being edited. */
  | { ok: false; reason: "changed" }
  /** Moving a day onto a date that already has a workout. */
  | { ok: false; reason: "occupied"; date: DateString }
  | { ok: false; reason: "failed"; detail: string };

/**
 * Write a day back, optionally under a new date, creating it if it is new.
 *
 * @param baseUpdatedAt the `updatedAt` the draft was made from, or `null` when the day
 *   is being created by hand (B-23). A day that has moved on since is refused rather
 *   than overwritten; a day being created that turns out to already exist is refused as
 *   `occupied` rather than overwriting whatever is there — which, on the create path, is
 *   most likely a session the bot logged while this one was being typed.
 */
export async function saveDay(
  uid: string,
  originalDate: DateString,
  draft: Workout,
  baseUpdatedAt: number | null,
  now = Date.now(),
): Promise<SaveResult> {
  const workouts = (date: string) => doc(db, "users", uid, "workouts", date);
  const creating = baseUpdatedAt === null;

  try {
    await runTransaction(db, async (tx) => {
      // Every read in a Firestore transaction must precede every write, so the whole
      // decision is made before anything is set or deleted.
      const moving = draft.date !== originalDate;

      if (creating) {
        // Nothing exists at `originalDate` to compare against, and nothing there is
        // ours to move. Only the date being written to matters — including when the
        // date field was walked back to an earlier day to backfill it.
        const destination = await tx.get(workouts(draft.date));
        if (destination.exists()) throw new Conflict("occupied", draft.date);
      } else {
        const current = await tx.get(workouts(originalDate));
        if (!current.exists()) throw new Conflict("changed");
        if ((current.data() as Workout).updatedAt !== baseUpdatedAt) throw new Conflict("changed");

        if (moving) {
          const destination = await tx.get(workouts(draft.date));
          if (destination.exists()) throw new Conflict("occupied", draft.date);
        }
      }

      const saved: Workout = {
        ...draft,
        id: draft.date,
        score: scoreWorkout(draft),
        // A day created here carries the moment it was written, not the zero the blank
        // draft was built with.
        createdAt: creating ? now : draft.createdAt,
        updatedAt: now,
      };

      if (moving && !creating) tx.delete(workouts(originalDate));
      tx.set(workouts(draft.date), saved);
    });
    return { ok: true };
  } catch (error) {
    if (error instanceof Conflict) {
      return error.date
        ? { ok: false, reason: "occupied", date: error.date }
        : { ok: false, reason: "changed" };
    }
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Delete a whole day.
 *
 * Unconditional, unlike a save: "delete this day" stays the user's intent even if the
 * bot appended to it a moment ago, and a compare-and-set here would only produce a
 * confusing refusal. The confirmation in the UI is what guards it.
 */
export async function deleteDay(uid: string, date: DateString): Promise<SaveResult> {
  try {
    await deleteDoc(doc(db, "users", uid, "workouts", date));
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/** Update the profile's current bodyweight. Past days keep what they were logged at (D-20). */
export async function setCurrentBodyweight(uid: string, kg: number): Promise<SaveResult> {
  try {
    await runTransaction(db, async (tx) => {
      tx.update(doc(db, "users", uid), { currentBodyweightKg: kg });
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Edit the answers a plan is built from, from Profile (B-44).
 *
 * The health answer passes through `normaliseHealth` here exactly as it does at
 * onboarding, so a note edited on Profile is trimmed and capped the same way — the two
 * doors must not store two shapes of the same answer (D-09).
 */
export async function saveTrainingAnswers(
  uid: string,
  patch: Pick<UserProfile, "bodyType" | "goals" | "health">,
): Promise<SaveResult> {
  const clean: Partial<UserProfile> = {};
  if (patch.bodyType !== undefined) clean.bodyType = patch.bodyType;
  if (patch.goals !== undefined && patch.goals.length > 0) clean.goals = patch.goals;
  if (patch.health !== undefined) clean.health = normaliseHealth(patch.health);
  try {
    await runTransaction(db, async (tx) => {
      tx.update(doc(db, "users", uid), clean);
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Save a plan the person has looked at and accepted (B-48).
 *
 * One batch: the plan document, and on the profile the answers it was built from plus the
 * preferences. Written together so `planIsStale` compares the plan against the answers it
 * was actually made from — a plan saved while the profile write failed would read as out
 * of date the moment it appeared.
 */
export async function savePlan(uid: string, plan: Plan): Promise<SaveResult> {
  const { bodyType, goals, health, prefs } = plan.builtFrom;
  try {
    const batch = writeBatch(db);
    batch.set(doc(db, "users", uid, "plan", "current"), plan);
    batch.update(doc(db, "users", uid), { bodyType, goals, health, planPrefs: prefs });
    await batch.commit();
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

export async function deletePlan(uid: string): Promise<SaveResult> {
  try {
    await deleteDoc(doc(db, "users", uid, "plan", "current"));
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * Ask `reviewPlan` to check a plan against the injury notes (B-47).
 *
 * Never throws and never loses the plan: anything that goes wrong — offline, refused,
 * a reply that is not a plan — hands back the plan it was given, marked `failed`, and
 * the screen offers to try again.
 */
export async function requestReview(plan: Plan): Promise<Plan> {
  try {
    const call = httpsCallable<{ plan: Plan }, { plan: unknown }>(functions, "reviewPlan", { timeout: 35_000 });
    const result = await call({ plan });
    return isPlan(result.data.plan) ? result.data.plan : { ...plan, review: "failed" };
  } catch {
    return { ...plan, review: "failed" };
  }
}

/**
 * Finish onboarding: one merge of everything that was answered, plus the timestamp the
 * gate reads (B-38, D-90).
 *
 * A single write at the end rather than one per question. The flow is four or five taps
 * long and a half-written profile is a worse thing to own than a flow that has to be
 * redone — and `onboardedAt` landing in the same write as the answers is what makes the
 * gate's "these questions have been asked" true at the moment it becomes true.
 *
 * `update`, not `set`: the document already exists (the bot's `ensureUser` or
 * `ensureProfile` made it) and carries a phone number, a timezone and a `createdAt` this
 * has no business replacing. Which fields are merged is `profilePatch`'s decision, not
 * this function's — including that a question the role was never asked leaves no field
 * behind.
 */
export async function completeOnboarding(
  uid: string,
  answers: OnboardingAnswers,
  now: Date,
): Promise<SaveResult> {
  try {
    const patch = profilePatch(answers, now.getFullYear(), now.getTime());
    await runTransaction(db, async (tx) => {
      tx.update(doc(db, "users", uid), patch);
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

class Conflict extends Error {
  constructor(
    readonly kind: "changed" | "occupied",
    readonly date?: DateString,
  ) {
    super(kind);
  }
}

/**
 * Ticking one set (B-34).
 *
 * A transaction rather than `saveDay`'s compare-and-set, deliberately. D-60 refuses a
 * whole-day save that raced another writer, because merging a draft is not well defined:
 * if a set was edited here and the same exercise changed there, no rule picks a winner
 * the user would recognise. A single-set append has no such ambiguity — it is exactly
 * what the bot has done since D-48 — so a WhatsApp message arriving while someone is
 * ticking through a session MERGES instead of being refused, which is what a person
 * standing in a gym with both clients open actually wants.
 *
 * The decisions live in `today.ts` as pure functions; all that happens here is read,
 * apply, write. `score` is always recomputed from the post-change document with
 * `scoreWorkout` and never by adding a delta — a denormalised score drifting from its
 * own sets is C-11's whole subject.
 */
export type TickResult = { ok: true } | { ok: false; detail: string };

async function mutateDay(
  uid: string,
  date: DateString,
  now: number,
  store: Firestore,
  /** `null` deletes the day; returning the input unchanged still writes, harmlessly. */
  change: (workout: Workout) => Workout | null,
  /** Builds the day when it does not exist yet, or null to make absence a no-op. */
  blank: (() => Workout) | null,
): Promise<TickResult> {
  try {
    await runTransaction(store, async (tx) => {
      const ref = doc(store, "users", uid, "workouts", date);
      const snapshot = await tx.get(ref);

      const current = snapshot.exists() ? (snapshot.data() as Workout) : blank?.() ?? null;
      // Unticking a set on a day that no longer exists is a no-op, not an error: the
      // screen is subscribed and is about to re-render without it anyway.
      if (!current) return;

      const next = change(current);
      if (!next) {
        if (snapshot.exists()) tx.delete(ref);
        return;
      }

      tx.set(ref, { ...next, id: date, score: scoreWorkout(next), updatedAt: now });
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, detail: error instanceof Error ? error.message : String(error) };
  }
}

export function addSet(
  uid: string,
  date: DateString,
  bodyweightKg: number,
  exercise: { catalogId: string; name: string; muscleGroup: MuscleGroup },
  set: ExerciseSet,
  now = Date.now(),
  store: Firestore = db,
): Promise<TickResult> {
  return mutateDay(
    uid,
    date,
    now,
    store,
    (workout) => withSetAdded(workout, exercise, set, () => crypto.randomUUID()),
    () => emptyWorkout(date, bodyweightKg, now),
  );
}

export function removeSet(
  uid: string,
  date: DateString,
  setId: string,
  now = Date.now(),
  store: Firestore = db,
): Promise<TickResult> {
  return mutateDay(uid, date, now, store, (workout) => withSetRemoved(workout, setId), null);
}

export function updateSet(
  uid: string,
  date: DateString,
  set: ExerciseSet,
  now = Date.now(),
  store: Firestore = db,
): Promise<TickResult> {
  return mutateDay(uid, date, now, store, (workout) => withSetUpdated(workout, set), null);
}

/**
 * The PT switch on Today (B-56). The same single-day transaction as a tick, so a bot
 * message landing at the same moment merges. Turning it on creates the day if needed;
 * turning it off on a day with nothing in it deletes the day.
 */
export function setPt(
  uid: string,
  date: DateString,
  bodyweightKg: number,
  on: boolean,
  now = Date.now(),
  store: Firestore = db,
): Promise<TickResult> {
  return mutateDay(
    uid,
    date,
    now,
    store,
    (workout) => withPt(workout, on),
    on ? () => emptyWorkout(date, bodyweightKg, now) : null,
  );
}

/**
 * Add a gym to the shared list, or find it if it is already there (B-52, D-103).
 *
 * The id is derived from the name and PIN, so "already there" is a read of one document.
 * The create can still lose a race to someone adding the same gym a moment earlier; the
 * rules refuse it as an update, and a second read finds theirs. `existed` lets the
 * picker say so instead of pretending it made one.
 */
export async function addGym(
  uid: string,
  name: string,
  pinCode: string,
): Promise<{ ok: true; gym: GymRef; existed: boolean } | { ok: false; detail: string }> {
  const id = gymId(name, pinCode);
  if (!id) return { ok: false, detail: "Use 2 to 60 characters, with at least one letter or digit." };
  const ref = doc(db, "gyms", id);
  const found = async () => {
    const snap = await getDoc(ref);
    return snap.exists() ? { id, name: String(snap.get("name")), pinCode: String(snap.get("pinCode")) } : null;
  };
  try {
    const existing = await found();
    if (existing) return { ok: true, gym: existing, existed: true };
    const trimmed = { name: name.trim(), pinCode: pinCode.trim() };
    await setDoc(ref, { ...trimmed, createdAt: serverTimestamp(), createdBy: uid });
    return { ok: true, gym: { id, ...trimmed }, existed: false };
  } catch (error) {
    const raced = await found().catch(() => null);
    if (raced) return { ok: true, gym: raced, existed: true };
    return { ok: false, detail: error instanceof Error ? error.message : String(error) };
  }
}

/** The gym a person goes to or runs, copied onto their profile. */
export async function saveGym(uid: string, gym: GymRef): Promise<SaveResult> {
  try {
    await runTransaction(db, async (tx) => {
      tx.update(doc(db, "users", uid), { gym });
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/**
 * What a trainee pays (B-52, D-104). The whole map is replaced, so turning PT off
 * removes it rather than leaving the old pack behind. Validated by core first: nothing
 * reaches the document that `isValidGymCosts` would refuse to read back.
 */
export async function saveGymCosts(uid: string, costs: GymCosts): Promise<SaveResult> {
  if (!isValidGymCosts(costs)) return { ok: false, reason: "failed", detail: "Those amounts are out of range." };
  try {
    await runTransaction(db, async (tx) => {
      tx.update(doc(db, "users", uid), { gymCosts: costs });
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

// ── The admin's exercise-list edits (B-66, D-118) ─────────────────────────────────────
//
// Validated by core's `prepareNewExercise` / `prepareRename` before they get here, and
// by firestore.rules when they arrive: only the admin's number may write, and an added
// exercise's factor and measure are fixed at creation.

/** Adds an exercise. `create` semantics: an id that already exists is refused by the rules. */
export async function addCustomExercise(uid: string, exercise: CustomExercise): Promise<SaveResult> {
  const { id, ...fields } = exercise;
  try {
    await setDoc(doc(db, "customExercises", id), { ...fields, createdAt: serverTimestamp(), createdBy: uid });
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/** A new name for an exercise — on its own document if added here, in `exerciseNames` if built in. */
export async function renameExercise(uid: string, id: string, name: string, aliases: string[]): Promise<SaveResult> {
  try {
    if (isCustomId(id)) {
      await updateDoc(doc(db, "customExercises", id), { name, aliases, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, "exerciseNames", id), { name, updatedAt: serverTimestamp(), updatedBy: uid });
    }
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}

/** Gives a built-in its original name back. */
export async function restoreExerciseName(id: string): Promise<SaveResult> {
  try {
    await deleteDoc(doc(db, "exerciseNames", id));
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: "failed", detail: error instanceof Error ? error.message : String(error) };
  }
}
